/**
 * StorageAdapter — Persists consent decisions in localStorage and a cookie
 * so they survive page navigations and can be read server-side.
 */

export interface ConsentDecisions {
  strictly_necessary: true; // Always true
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
  social: boolean;
}

export interface StoredConsent {
  decisions: ConsentDecisions;
  timestamp: number;
  bannerVersion: number;
  regulation: string;
  sessionId: string;
}

const STORAGE_KEY = "pb_consent";
const COOKIE_NAME = "pb_consent";
const CONSENT_VERSION = 1;
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 1 year

export class StorageAdapter {
  private domainId: string;

  constructor(domainId: string) {
    this.domainId = domainId;
  }

  load(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(`${STORAGE_KEY}_${this.domainId}`);
      if (!raw) return null;
      const data = JSON.parse(raw) as StoredConsent;
      // Validate structure
      if (!data.decisions || typeof data.timestamp !== "number") return null;
      return data;
    } catch {
      return null;
    }
  }

  save(consent: StoredConsent): void {
    try {
      const key = `${STORAGE_KEY}_${this.domainId}`;
      localStorage.setItem(key, JSON.stringify(consent));
      // Also set a cookie for server-side reading
      this.setCookie(consent);
    } catch {
      // Ignore storage errors (private mode, etc.)
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(`${STORAGE_KEY}_${this.domainId}`);
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    } catch {
      // Ignore
    }
  }

  private setCookie(consent: StoredConsent): void {
    // Shape must match what the server SDK (@pritect-beacon/next getServerConsent)
    // reads: { decisions, consentedAt, sessionId }. The old compact { d, t, v }
    // shape never matched, so server-side consent gating always saw null.
    const value = encodeURIComponent(JSON.stringify({
      decisions: consent.decisions,
      consentedAt: new Date(consent.timestamp).toISOString(),
      sessionId: consent.sessionId,
    }));
    const expires = new Date(Date.now() + MAX_AGE_SECONDS * 1000).toUTCString();
    document.cookie = `${COOKIE_NAME}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /**
   * A stable per-browser, per-domain visitor id used only for deterministic
   * A/B variant assignment (so a returning visitor always sees the same
   * variant). First-party, non-PII, cleared by revokeConsent (pb_ prefix).
   */
  getVisitorId(): string {
    const key = `pb_vid_${this.domainId}`;
    try {
      let vid = localStorage.getItem(key);
      if (!vid) {
        vid = StorageAdapter.generateSessionId();
        localStorage.setItem(key, vid);
      }
      return vid;
    } catch {
      return StorageAdapter.generateSessionId();
    }
  }

  static generateSessionId(): string {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    return Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  static getVersion(): number {
    return CONSENT_VERSION;
  }
}
